import React, { useRef, useState } from 'react';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import { ExtendedMarkdown } from '../../MessageContent/ExtendedMarkdown';
import { takeScreenshot } from '../../../../../utils/screenshotUtils';
import { useTextPDFPrint } from '../../../../../utils/pdfUtils';
import { PrintWrapper } from '../../MessageContent/markdown/PrintWrapper';

import { useTranslation } from 'react-i18next';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

export function safeJSONParse(content: unknown): unknown {
  if (typeof content !== 'string') {
    return content;
  }
  try {
    return JSON.parse(content);
  } catch {
    return undefined;
  }
}

export function extractMarkdownFromToolUse(
  toolUse: ToolUseBlock,
): string | undefined {
  let property: string | undefined;
  if (toolUse && typeof toolUse.meta === 'object' && toolUse.meta !== null) {
    // @ts-expect-error: meta is typed as unknown, but we expect markdownProperty to be present if set by user
    property = toolUse.meta.markdownProperty;
  }
  const markdown = property
    ? (toolUse.input[property] as string)
    : (toolUse.input.markdown as string);
  return markdown || undefined;
}

function extractFileNameFromToolUse(toolUse: ToolUseBlock): string {
  if (toolUse.input?.fileName) {
    return toolUse.input.fileName as string;
  }

  return 'tool-result.pdf';
}

export const MarkdownRenderer: React.FC<Props> = ({ toolUse }) => {
  const { t } = useTranslation('pleri/chat');
  const markdown = extractMarkdownFromToolUse(toolUse);
  const contentRef = useRef<HTMLDivElement>(null);
  const fileName = extractFileNameFromToolUse(toolUse);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Use text-based PDF printing for reports
  const handleTextPrint = useTextPDFPrint(contentRef, {
    filename: fileName.replace(/\.[^.]+$/, '.pdf'),
  });

  const handleScreenshot = async () => {
    if (!contentRef.current || isCapturing) {
      return;
    }

    setIsCapturing(true);
    try {
      await takeScreenshot(contentRef.current, {
        filename: fileName.replace(/\.[^.]+$/, '.png'),
        scale: 2,
        backgroundColor: '#ffffff',
      });
    } catch (error) {
      console.error('Failed to take screenshot:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  const handlePDFGeneration = async () => {
    if (isGeneratingPDF) {
      return;
    }

    setIsGeneratingPDF(true);
    try {
      await handleTextPrint();
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!markdown) {
    return null;
  }
  return (
    <div className="p-6">
      <div className="mb-2 flex justify-end gap-2">
        <button
          className="rounded bg-green-600 px-3 py-1 text-sm font-medium text-white shadow hover:bg-green-700 disabled:opacity-50"
          onClick={handleScreenshot}
          disabled={isCapturing}
        >
          {isCapturing ? t('capturing') : t('screenshot')}
        </button>
        <button
          className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:opacity-50"
          onClick={handlePDFGeneration}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? t('generating') : t('downloadPdf')}
        </button>
      </div>
      <div className="prose prose-sm max-w-none rounded-lg border border-gray-200 bg-white p-4 antialiased dark:border-gray-700 dark:bg-gray-900 print:border-none">
        <div
          ref={contentRef}
          className="prose prose-sm max-w-none bg-white p-4 antialiased dark:bg-gray-900"
        >
          <PrintWrapper>
            <ExtendedMarkdown content={markdown} />
          </PrintWrapper>
        </div>
      </div>
    </div>
  );
};
